/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/A1-A3/A1A3.tsx.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./A1A3.css";
import FormFieldInfoTooltip from "../../../../../components/common/FormFieldInfoTooltip";
import {
  getSubmissionById,
  saveA1A3Summary,
} from "../../../../../services/submissionService";
import Button from "../../../../../components/common/Button";
import { fetchUnitIdToSymbolMap } from "../../../../../utils/fetchUnitIdToSymbolMap";
import {
  buildA1A3CarbonUnitOptions,
  buildA1A3EnergyUnitOptions,
  computedEmbodiedCarbonPerFinished,
  computedEmbodiedEnergyPerFinished,
  readBackgroundFinishedDeclaredQty,
  resolveFinishedQtyDenominatorSymbol,
  sumA1A2A3EmbodiedCarbonTotal,
  sumA1A2A3EmbodiedEnergyMj,
  syncA1A3UnitSelection,
} from "./a1a3SummaryFromSubmission";
import { shouldUpgradeBareUnitToPerDenominator } from "../shared/contributorPerFunctionalUnitUnits";
import { EMBODIED_CARBON_PER_FUNCTIONAL_UNIT_LABEL } from "../shared/contributorFieldDisplayLabels";

interface A1A3DataProps {
  submissionId?: string;
  onNextStage: () => void;
}

function formatNum(n: number | null | undefined, digits = 3): string {
  if (n == null || !Number.isFinite(n)) return "";
  return String(Math.round(n * 10 ** digits) / 10 ** digits);
}

const VALUE_FIELD_TOOLTIP =
  "Value (editable — default is per finished qty when declared quantity is set)";

const CARBON_VALUE_FIELD_LABEL = EMBODIED_CARBON_PER_FUNCTIONAL_UNIT_LABEL;

function SummaryValueFieldLabel({
  htmlFor,
  label,
}: {
  htmlFor: string;
  label: string;
}) {
  return (
    <label htmlFor={htmlFor} className="input-label a-field-label-row a1a3-value-field-label">
      <span>{label}</span>
      <FormFieldInfoTooltip text={VALUE_FIELD_TOOLTIP} />
    </label>
  );
}

const A1A3: React.FC<A1A3DataProps> = ({ submissionId, onNextStage }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [energyBase, setEnergyBase] = useState("");
  const [carbonBase, setCarbonBase] = useState("");
  const [unitIdToSymbol, setUnitIdToSymbol] = useState<Record<number, string>>(
    {}
  );
  const [energyUnit, setEnergyUnit] = useState("");
  const [carbonUnit, setCarbonUnit] = useState("");

  const totalEnergyMj = useMemo(
    () => (data ? sumA1A2A3EmbodiedEnergyMj(data) : 0),
    [data]
  );

  const totalCarbonRef = useMemo(
    () => (data ? sumA1A2A3EmbodiedCarbonTotal(data) : 0),
    [data]
  );

  const computedEnergyPerFinished = useMemo(
    () => (data ? computedEmbodiedEnergyPerFinished(data) : null),
    [data]
  );

  const computedCarbonPerFinished = useMemo(
    () => (data ? computedEmbodiedCarbonPerFinished(data) : null),
    [data]
  );

  const resetEnergyToCalculated = useCallback(() => {
    if (computedEnergyPerFinished != null) {
      setEnergyBase(formatNum(computedEnergyPerFinished));
    } else {
      setEnergyBase(totalEnergyMj > 0 ? formatNum(totalEnergyMj) : "");
    }
  }, [computedEnergyPerFinished, totalEnergyMj]);

  const resetCarbonToCalculated = useCallback(() => {
    if (computedCarbonPerFinished != null) {
      setCarbonBase(formatNum(computedCarbonPerFinished));
    } else {
      setCarbonBase(totalCarbonRef > 0 ? formatNum(totalCarbonRef) : "");
    }
  }, [computedCarbonPerFinished, totalCarbonRef]);

  useEffect(() => {
    const load = async () => {
      if (!submissionId) {
        setLoading(false);
        return;
      }

      try {
        const [res, idMap] = await Promise.all([
          getSubmissionById(submissionId),
          fetchUnitIdToSymbolMap(),
        ]);
        setUnitIdToSymbol(idMap);
        if (res.data?.success) {
          const d = res.data.data;
          setData(d);

          const summary = d?.a1a3Summary;
          const perE = computedEmbodiedEnergyPerFinished(d);
          const perC = computedEmbodiedCarbonPerFinished(d);
          const sumE = sumA1A2A3EmbodiedEnergyMj(d);
          const sumC = sumA1A2A3EmbodiedCarbonTotal(d);

          const savedEnergy =
            summary?.embodiedEnergyValue != null &&
            summary?.embodiedEnergyValue !== ""
              ? String(summary.embodiedEnergyValue).trim()
              : "";
          const savedCarbon =
            summary?.embodiedCarbonValue != null &&
            summary?.embodiedCarbonValue !== ""
              ? String(summary.embodiedCarbonValue).trim()
              : "";

          setEnergyBase(
            savedEnergy !== ""
              ? savedEnergy
              : perE != null
                ? formatNum(perE)
                : sumE > 0
                  ? formatNum(sumE)
                  : ""
          );
          setCarbonBase(
            savedCarbon !== ""
              ? savedCarbon
              : perC != null
                ? formatNum(perC)
                : sumC > 0
                  ? formatNum(sumC)
                  : ""
          );

          const den = resolveFinishedQtyDenominatorSymbol(d, idMap);
          const eOpts = buildA1A3EnergyUnitOptions(den);
          const cOpts = buildA1A3CarbonUnitOptions(den);
          const preferPer = readBackgroundFinishedDeclaredQty(d) != null;
          const savedEUnit = String(summary?.embodiedEnergyUnit ?? "").trim();
          const savedCUnit = String(summary?.embodiedCarbonUnit ?? "").trim();
          setEnergyUnit(
            syncA1A3UnitSelection(savedEUnit, "", eOpts, preferPer)
          );
          setCarbonUnit(
            syncA1A3UnitSelection(savedCUnit, "", cOpts, preferPer)
          );
        }
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [submissionId]);

  const denominatorSymbol = useMemo(
    () =>
      data
        ? resolveFinishedQtyDenominatorSymbol(data, unitIdToSymbol)
        : null,
    [data, unitIdToSymbol]
  );

  const energyUnitOptions = useMemo(
    () => buildA1A3EnergyUnitOptions(denominatorSymbol),
    [denominatorSymbol]
  );

  const carbonUnitOptions = useMemo(
    () => buildA1A3CarbonUnitOptions(denominatorSymbol),
    [denominatorSymbol]
  );

  useEffect(() => {
    if (!denominatorSymbol?.trim()) return;
    setEnergyUnit((cur) =>
      shouldUpgradeBareUnitToPerDenominator(cur, energyUnitOptions)
        ? syncA1A3UnitSelection(cur, cur, energyUnitOptions, true)
        : cur
    );
    setCarbonUnit((cur) =>
      shouldUpgradeBareUnitToPerDenominator(cur, carbonUnitOptions)
        ? syncA1A3UnitSelection(cur, cur, carbonUnitOptions, true)
        : cur
    );
  }, [denominatorSymbol, energyUnitOptions, carbonUnitOptions]);

  if (loading) return <p className="loading-text">Loading summary...</p>;
  if (!data) return <p>No data found.</p>;

  const handleNext = async () => {
    if (!submissionId) return;

    const ee = Number(energyBase);
    const ec = Number(carbonBase);
    if (!Number.isFinite(ee) || !Number.isFinite(ec)) {
      window.alert("Please enter valid numbers for embodied energy and embodied carbon.");
      return;
    }

    const eu = energyUnit.trim();
    const cu = carbonUnit.trim();
    if (
      !eu ||
      !cu ||
      !energyUnitOptions.some((o) => o.value === eu) ||
      !carbonUnitOptions.some((o) => o.value === cu)
    ) {
      window.alert("Please select valid units for embodied energy and embodied carbon.");
      return;
    }

    await saveA1A3Summary(submissionId, {
      embodiedEnergyValue: ee,
      embodiedEnergyUnit: eu,
      embodiedCarbonValue: ec,
      embodiedCarbonUnit: cu,
    });

    onNextStage();
  };

  return (
    <div className="a1a3-container contributor-stage-form">
      <h2 className="form-heading">A1 – A3 Summary</h2>

      {/* ENERGY */}
      <div className="a-section">
        <h3 className="a-section-title">Embodied Energy (A1–A3)</h3>
        <div className="a1a3-value-unit-stack">
          <div className="a1a3-value-unit-labels">
            <div className="a1a3-label-cell">
              <SummaryValueFieldLabel htmlFor="a1a3-summary-energy" label="Value" />
            </div>
            <div className="a1a3-label-cell">
              <label htmlFor="a1a3-summary-energy-unit" className="input-label a1a3-unit-label">
                Unit
              </label>
            </div>
          </div>
          <div className="a1a3-value-unit-inputs">
            <div className="a-field">
              <input
                id="a1a3-summary-energy"
                type="number"
                step="any"
                value={energyBase}
                onChange={(e) => setEnergyBase(e.target.value)}
              />
            </div>
            <div className="a-field">
              <select
                id="a1a3-summary-energy-unit"
                className="a1a3-unit-select"
                value={
                  energyUnitOptions.some((o) => o.value === energyUnit)
                    ? energyUnit
                    : energyUnitOptions[0]?.value ?? ""
                }
                onChange={(e) => setEnergyUnit(e.target.value)}
              >
                {energyUnitOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="a1a3-actions-row">
          <button type="button" className="a1a3-link-btn" onClick={resetEnergyToCalculated}>
            Use calculated value
          </button>
        </div>
      </div>

      {/* CARBON */}
      <div className="a-section">
        <h3 className="a-section-title">Embodied Carbon (A1–A3)</h3>
        <div className="a1a3-value-unit-stack">
          <div className="a1a3-value-unit-labels">
            <div className="a1a3-label-cell">
              <SummaryValueFieldLabel
                htmlFor="a1a3-summary-carbon"
                label={CARBON_VALUE_FIELD_LABEL}
              />
            </div>
            <div className="a1a3-label-cell">
              <label htmlFor="a1a3-summary-carbon-unit" className="input-label a1a3-unit-label">
                Unit
              </label>
            </div>
          </div>
          <div className="a1a3-value-unit-inputs">
            <div className="a-field">
              <input
                id="a1a3-summary-carbon"
                type="number"
                step="any"
                value={carbonBase}
                onChange={(e) => setCarbonBase(e.target.value)}
              />
            </div>
            <div className="a-field">
              <select
                id="a1a3-summary-carbon-unit"
                className="a1a3-unit-select"
                value={
                  carbonUnitOptions.some((o) => o.value === carbonUnit)
                    ? carbonUnit
                    : carbonUnitOptions[0]?.value ?? ""
                }
                onChange={(e) => setCarbonUnit(e.target.value)}
              >
                {carbonUnitOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="a1a3-actions-row">
          <button type="button" className="a1a3-link-btn" onClick={resetCarbonToCalculated}>
            Use calculated value
          </button>
        </div>
      </div>

      <div className="button-row">
        <Button label="Next" onClick={handleNext} />
      </div>
    </div>
  );
};

/** React component — A1A3. */
export default A1A3;
